"use client";

import { motion } from "framer-motion";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

// BulletPoint component with TypeScript interface
interface BulletPointProps {
  children: React.ReactNode;
  number?: number;
}

const BulletPoint = ({ children, number }: BulletPointProps) => (
  <li className="relative pl-6 mb-2">
    {number ? (
      <span className="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: colors.primaryLight }}>
        {number}
      </span>
    ) : (
      <span className="absolute left-0 top-2 h-2 w-2 rounded-full" style={{ backgroundColor: colors.primaryLight }}></span>
    )}
    {children}
  </li>
);

export default function PrivacyPolicy() {
  return (
    <section className="py-20" style={{ backgroundColor: colors.white }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full px-6 py-2 text-sm font-semibold uppercase tracking-wider" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
            Last Updated: [Date]
          </span>
          <h2 className="text-4xl font-bold sm:text-5xl" style={{ color: colors.textDark }}>
            Privacy
            <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
              {" "}Policy
            </span>
          </h2>
          <div className="mx-auto mt-6 h-1.5 w-24 rounded-full" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-lg" style={{ color: colors.textDark }}>
            Protecting your personal information is our top priority
          </p>
        </motion.div>

        {/* Policy Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="prose-lg mx-auto max-w-3xl"
          style={{ color: colors.textDark }}
        >
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>Introduction</h3>
            <p>
              At Yegna Microfinance, we are committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit 
              our website or use our services.
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>Information We Collect</h3>
            <ul className="space-y-2">
              <BulletPoint>
                <strong>Personal Information</strong>: Name, email, phone number, identification documents
              </BulletPoint>
              <BulletPoint>
                <strong>Financial Information</strong>: Bank details, transaction history, credit information
              </BulletPoint>
              <BulletPoint>
                <strong>Technical Data</strong>: IP address, browser type, device information
              </BulletPoint>
            </ul>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>How We Use Your Information</h3>
            <ol className="space-y-3">
              <BulletPoint number={1}>
                Process transactions and provide services
              </BulletPoint>
              <BulletPoint number={2}>
                Improve our products and services
              </BulletPoint>
              <BulletPoint number={3}>
                Comply with legal obligations
              </BulletPoint>
            </ol>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>Data Security</h3>
            <p>
              We implement security measures including:
            </p>
            <div className="grid gap-6 mt-4 sm:grid-cols-2">
              <div>
                <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Technical Measures</h4>
                <ul className="space-y-2">
                  <BulletPoint>SSL Encryption</BulletPoint>
                  <BulletPoint>Firewall Protection</BulletPoint>
                  <BulletPoint>Regular Security Audits</BulletPoint>
                </ul>
              </div>
              <div>
                <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Organizational Measures</h4>
                <ul className="space-y-2">
                  <BulletPoint>Staff Training</BulletPoint>
                  <BulletPoint>Access Controls</BulletPoint>
                  <BulletPoint>Data Minimization</BulletPoint>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>Your Rights</h3>
            <div className="rounded-lg p-6" style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}20` }}>
              <ul className="space-y-3">
                <BulletPoint>Right to access your data</BulletPoint>
                <BulletPoint>Right to request correction</BulletPoint>
                <BulletPoint>Right to delete information</BulletPoint>
                <BulletPoint>Right to object to processing</BulletPoint>
              </ul>
            </div>
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="inline-block rounded-2xl p-0.5 shadow-lg" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
              <div className="rounded-2xl px-8 py-8 sm:px-12 sm:py-10" style={{ backgroundColor: colors.white }}>
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="h-8 w-8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="mt-6 text-2xl font-bold" style={{ color: colors.textDark }}>
                  Privacy Concerns?
                </h3>
                <p className="mt-3" style={{ color: colors.textDark }}>
                  Contact our Data Protection Officer
                </p>
                <div className="mt-6">
                  <a 
                    href="mailto:privacy@yegnamicrofinance.com" 
                    className="font-medium transition-colors hover:opacity-80"
                    style={{ color: colors.primary }}
                  >
                    privacy@yegnamicrofinance.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}